import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { useState, useEffect } from "react"
import { toast } from "sonner"
import { Loader2 } from "lucide-react"
import { Input } from "@/components/ui/input"
import { useFibe } from "@/hooks/use-loan"

type Props = {
	mobileNumber: string
	setOtpVerified: (otpVerified: boolean) => void
}

const VerifyOTP = ({ mobileNumber, setOtpVerified }: Props) => {
	const [otp, setOtp] = useState("")
	const [open, setOpen] = useState(false)
	const [counter, setCounter] = useState(60)
	const [otpSent, setOtpSent] = useState(false)
	const { useSendOTP } = useFibe()
	const { useVerifyOTP } = useFibe()
	const {
		mutate: verifyOtp,
		isPending: verifyOtpPending,
		isSuccess: verifyOtpSuccess,
	} = useVerifyOTP()
	const {
		mutate: sendOtp,
		isPending: sendOtpPending,
		isSuccess: sendOtpSuccess,
	} = useSendOTP()

	const handleVerify = () => {
		if (otp.length !== 12) {
			toast.error("Invalid OTP")
			return
		}
		verifyOtp({
			mobileNumber,
			otpCode: otp,
		})
	}

	// set otpVerified to false when the mobileNumber changes
	useEffect(() => {
		if (mobileNumber.length !== 10) {
			setOtpVerified(false)
		}
	}, [mobileNumber])

	useEffect(() => {
		if (verifyOtpSuccess) {
			setOpen(false)
			setOtpVerified(true)
		}
	}, [verifyOtpSuccess])

	useEffect(() => {
		// Only run the timer if the counter is greater than 0
		if (counter > 0) {
			const timer = setInterval(() => {
				setCounter(prevCounter => prevCounter - 1)
			}, 1000)

			// Cleanup function: clears the interval when the component unmounts
			// or when the counter state changes.
			return () => clearInterval(timer)
		}
	}, [counter])

	useEffect(() => {
		if (sendOtpSuccess && mobileNumber.length === 10) {
			setOtpSent(true)
		}
	}, [sendOtpSuccess, mobileNumber])

	const handleSendOTP = () => {
		if (mobileNumber.length !== 10) {
			toast.error("Enter valid mobile number at applicant details")
			return
		}
		sendOtp({
			mobileNumber,
			isResendOtp: otpSent ? true : false,
		})
		if (sendOtpSuccess && !sendOtpPending) {
			setCounter(60)
		}
	}

	return (
		<Dialog
			open={open}
			onOpenChange={setOpen}
		>
			<DialogTrigger asChild>
				<Button
					variant="outline"
					onClick={e => {
						e.stopPropagation()
						if (mobileNumber.length !== 10) {
							toast.error(
								"Enter valid mobile number at applicant details",
							)
							setOpen(false)
							return
						}
						setOpen(true)
					}}
					disabled={
						sendOtpPending ||
						verifyOtpPending ||
						mobileNumber.length !== 10
					}
				>
					Verify
				</Button>
			</DialogTrigger>
			<DialogContent className="mx-auto w-[500px]">
				<DialogHeader>
					<DialogTitle>Verify OTP</DialogTitle>
					<DialogDescription>
						Please verify applicant mobile number to continue.
					</DialogDescription>
				</DialogHeader>
				<div className="flex-col items-center space-y-4">
					{otpSent && (
						<Input
							type="text"
							placeholder="Enter OTP"
							disabled={verifyOtpPending || sendOtpPending}
							value={otp}
							onChange={e => setOtp(e.target.value)}
						/>
					)}
					{otpSent ? (
						<Button
							onClick={handleVerify}
							disabled={verifyOtpPending}
						>
							Verify OTP{" "}
							{verifyOtpPending && (
								<Loader2 className="ml-2 h-4 w-4 animate-spin" />
							)}
						</Button>
					) : (
						<Button
							onClick={handleSendOTP}
							disabled={sendOtpPending}
						>
							Send OTP{" "}
							{sendOtpPending && (
								<Loader2 className="ml-2 h-4 w-4 animate-spin" />
							)}
						</Button>
					)}
				</div>
				<DialogFooter>
					{otpSent && !verifyOtpPending && (
						<Button
							onClick={handleSendOTP}
							disabled={
								sendOtpPending || counter > 0 || sendOtpPending
							}
						>
							Re-send OTP {counter > 0 && `in ${counter} seconds`}{" "}
							{sendOtpPending && (
								<Loader2 className="ml-2 h-4 w-4 animate-spin" />
							)}
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default VerifyOTP
